import { useEffect, useState } from 'react'
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Fab,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { AssistantDrawer } from '../components/AssistantDrawer'
import { VigiaEsgotadosPopup } from '../components/VigiaEsgotadosPopup'
import { RequirePanel } from '../components/RequirePanel'
import { BiDateRangeField } from '../components/BiDateRangeField'
import { CanalFilterField } from '../components/CanalFilterField'
import { BI_DRAWER_WIDTH, DomainNav } from '../components/DomainNav'
import { FiestaBrandLogo } from '../components/FiestaBrandLogo'
import { Hicon } from '../components/Hicon'
import { LaneSwitch } from '../components/LaneSwitch'
import { PanelInsightsDrawer } from '../components/PanelInsightsDrawer'
import { SourceCardsBar } from '../components/SourceCardsBar'
import { SourceProvenanceStrip } from '../components/SourceProvenanceStrip'
import { UiVisibilityDialog } from '../components/UiVisibilityDialog'
import { useBiAuth } from '../state/BiAuthContext'
import { useBiData } from '../state/BiDataContext'
import { useBiSource } from '../state/BiSourceContext'
import { useBiTheme } from '../state/BiThemeContext'
import {
  isNavPathHidden,
  useBiUiVisibility,
} from '../state/BiUiVisibilityContext'
import { usePanelInsights } from '../state/PanelInsightsContext'
import { THEME_OPTIONS, type BiThemeId } from '../theme'
import type { Periodo } from '../api/bridge'

/** Paridade Financial (`MainLayout` Toolbar = 5rem): chat fica abaixo do header. */
const BI_APP_BAR_HEIGHT = '5rem'

export function BiLayout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isNavMobile = useMediaQuery(theme.breakpoints.down('md'))
  const embedded = new URLSearchParams(window.location.search).get('embed') === '1'
  const [navOpen, setNavOpen] = useState(false)
  const [assistantOpen, setAssistantOpen] = useState(false)
  const [assistantExpanded, setAssistantExpanded] = useState(false)
  const [assistantPrompt, setAssistantPrompt] = useState<string | null>(null)
  const { themeId, setThemeId } = useBiTheme()
  const { user, logout } = useBiAuth()
  const { prefs } = useBiUiVisibility()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [userMenuEl, setUserMenuEl] = useState<null | HTMLElement>(null)
  const isSystemRoute =
    pathname === '/usuarios' ||
    pathname.startsWith('/usuarios/') ||
    pathname === '/perfil' ||
    pathname === '/configuracoes'
  const {
    periodo,
    setPeriodo,
    periodoOptions,
    customRange,
    setCustomRange,
    canalSelect,
    setCanalFilter,
    marketplaceOptions,
    loading,
    refresh,
    data,
  } = useBiData()
  const { isFiestaActive, activeSource } = useBiSource()
  const { aberto: insightsAberto, fechar: fecharInsights } = usePanelInsights()

  useEffect(() => {
    const openAssistant = (event: Event) => {
      const detail = (event as CustomEvent<{ prompt?: string }>).detail
      setAssistantPrompt(detail?.prompt || null)
      setAssistantOpen(true)
    }
    window.addEventListener('fiesta:open-rica', openAssistant)
    return () => window.removeEventListener('fiesta:open-rica', openAssistant)
  }, [])

  useEffect(() => {
    if (isNavPathHidden(pathname, prefs)) {
      navigate('/', { replace: true })
    }
  }, [pathname, prefs, navigate])

  // Paridade Financial: mudar de página no menu fecha o chat (e a gaveta de insights)
  useEffect(() => {
    setAssistantOpen(false)
    setAssistantExpanded(false)
    fecharInsights()
  }, [pathname, fecharInsights])

  // Chat e gaveta de insights ocupam o mesmo lado: abrir um fecha o outro.
  useEffect(() => {
    if (insightsAberto) {
      setAssistantOpen(false)
      setAssistantExpanded(false)
    }
  }, [insightsAberto])
  useEffect(() => {
    if (assistantOpen) fecharInsights()
  }, [assistantOpen, fecharInsights])

  useEffect(() => {
    if (window.parent === window) return
    window.parent.postMessage(
      {
        source: 'fiesta-rica',
        type: 'state',
        open: assistantOpen,
        expanded: assistantExpanded,
      },
      '*',
    )
  }, [assistantOpen, assistantExpanded])

  const closeAssistant = () => {
    setAssistantOpen(false)
    setAssistantExpanded(false)
  }

  useEffect(() => {
    const onMsg = (event: MessageEvent) => {
      const data = event.data
      if (!data || data.source !== 'fiesta-rica' || data.type !== 'toggle-expand') return
      setAssistantExpanded((v) => !v)
    }
    window.addEventListener('message', onMsg)
    return () => window.removeEventListener('message', onMsg)
  }, [])

  const openAssistantManually = () => {
    setAssistantPrompt(null)
    setAssistantOpen(true)
  }

  const emptyPeriod =
    isFiestaActive &&
    !loading &&
    !!data &&
    Number(data.vendas || 0) === 0 &&
    Number(data.pedidos || 0) === 0
  const weekToDateOnlyToday =
    !customRange &&
    periodo === 'semana' &&
    !!data?.rangeLabel &&
    data.rangeLabel.includes('→') &&
    data.rangeLabel.split(/\s*→\s*/)[0]?.trim() === data.rangeLabel.split(/\s*→\s*/)[1]?.trim()

  const drawerPaperSx = {
    width: BI_DRAWER_WIDTH,
    boxSizing: 'border-box' as const,
    borderRight: 'none',
    backgroundImage: 'none',
  }

  // Embebido no Financeiro: sidebar do BI some — o nav é o do S2; o header
  // ocupa 100% do iframe (paridade com a AppBar do Financeiro).
  const showBiSidebar = !embedded

  // Gavetas da direita (chat e insights) abaixo do header, com a sidebar
  // desktop sempre fora do modal/backdrop — como no Financial.
  const rightDrawerModalSx = (() => {
    const leftPad = showBiSidebar && !isNavMobile ? BI_DRAWER_WIDTH : 0
    if (embedded) {
      return leftPad
        ? {
            inset: `0 0 0 ${leftPad}px`,
            left: leftPad,
            right: 0,
            width: 'auto',
            '& .MuiBackdrop-root': { left: leftPad },
          }
        : undefined
    }
    return {
      top: BI_APP_BAR_HEIGHT,
      inset: `${BI_APP_BAR_HEIGHT} 0 0 ${leftPad}px`,
      left: leftPad,
      right: 0,
      width: 'auto',
      '& .MuiBackdrop-root': {
        top: BI_APP_BAR_HEIGHT,
        left: leftPad,
      },
    }
  })()

  const contentColumn = (
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: {
          xs: '100%',
          md: showBiSidebar ? `calc(100% - ${BI_DRAWER_WIDTH}px)` : '100%',
        },
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: 0,
          left: 0,
          right: 0,
          // Acima do modal do chat — header clicável (Fiesta IA, tema, conta)
          zIndex: (t) => t.zIndex.modal + 1,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
          useFlexGap
          flexWrap="nowrap"
          sx={{
            width: '100%',
            px: { xs: 1.5, sm: 2 },
            boxSizing: 'border-box',
            minHeight: BI_APP_BAR_HEIGHT,
            height: BI_APP_BAR_HEIGHT,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            {isNavMobile && showBiSidebar ? (
              <IconButton
                color="inherit"
                edge="start"
                aria-label="Abrir menu"
                onClick={() => setNavOpen(true)}
                sx={{ flexShrink: 0 }}
              >
                <Hicon name="menu" sx={{ fontSize: 24 }} />
              </IconButton>
            ) : null}
            <FiestaBrandLogo
              height={36}
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, maxWidth: 200 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  lineHeight: 1.2,
                  fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  fontFamily: '"Outfit", sans-serif',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 700,
                }}
              >
                Fiesta BI
              </Typography>
            </Box>
          </Stack>

          {!embedded ? <LaneSwitch /> : null}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ flexShrink: 0, flex: 1, minWidth: 0 }}
          >
            {!embedded ? (
              <TextField
                select
                size="small"
                label="Tema"
                value={themeId}
                onChange={(e) => setThemeId(e.target.value as BiThemeId)}
                sx={{ minWidth: { xs: 100, sm: 120 } }}
              >
                {THEME_OPTIONS.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <Button
              size="small"
              variant="text"
              onClick={refresh}
              disabled={loading}
              sx={{ flexShrink: 0, textTransform: 'none' }}
            >
              {isMobile ? '↻' : 'Atualizar'}
            </Button>
            {!isMobile ? (
              <Button variant="contained" onClick={openAssistantManually} sx={{ flexShrink: 0 }}>
                Fiesta IA
              </Button>
            ) : null}
            <Typography
              variant="body2"
              sx={{ display: { xs: 'none', sm: 'block' }, mr: 0.5, fontWeight: 500 }}
            >
              {user?.nome || user?.email || 'Utilizador'}
            </Typography>
            <IconButton
              aria-label="Conta"
              onClick={(e) => setUserMenuEl(e.currentTarget)}
              sx={{ flexShrink: 0, p: 0.5 }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'secondary.main',
                }}
              >
                <Hicon name="user" sx={{ fontSize: '1.125rem', color: 'common.white' }} />
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={userMenuEl}
              open={Boolean(userMenuEl)}
              onClose={() => setUserMenuEl(null)}
              onClick={() => setUserMenuEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              // AppBar usa modal+1 (acima do chat); o Menu padrão fica atrás do header.
              slotProps={{
                root: {
                  sx: { zIndex: (t) => t.zIndex.modal + 2 },
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  setUserMenuEl(null)
                  navigate('/perfil')
                }}
              >
                <ListItemIcon>
                  <Hicon name="user" sx={{ fontSize: '1.25rem' }} />
                </ListItemIcon>
                Perfil
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setUserMenuEl(null)
                  navigate('/configuracoes')
                }}
              >
                <ListItemIcon>
                  <Hicon name="settings" sx={{ fontSize: '1.25rem' }} />
                </ListItemIcon>
                Configurações
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={async () => {
                  setUserMenuEl(null)
                  await logout()
                  navigate('/login', { replace: true })
                }}
              >
                <ListItemIcon>
                  <Hicon name="logout" sx={{ fontSize: '1.25rem' }} />
                </ListItemIcon>
                Sair
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>
      </AppBar>

      {!isSystemRoute && prefs.fonteDeDados ? <SourceCardsBar /> : null}

      {!isSystemRoute && prefs.proveniencia ? <SourceProvenanceStrip /> : null}

      {/* Filtros de período / canal — ocultos em páginas de sistema.
          pt = mesmo que o main abaixo → espaço acima da barra = espaço até o conteúdo. */}
      {!isSystemRoute ? (
      <Box
        sx={{
          width: '100%',
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 1.5, sm: 2 },
          pb: 0,
          boxSizing: 'border-box',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{
            px: { xs: 1.25, sm: 1.5 },
            py: 1.15,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (t) =>
              t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(15, 118, 110, 0.18)',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
          }}
        >
          {isMobile ? (
            <>
              <TextField
                select
                size="small"
                label="Período"
                value={customRange ? 'custom' : periodo}
                onChange={(e) => {
                  const v = e.target.value
                  if (v === 'custom') return
                  setPeriodo(v as Periodo)
                }}
                sx={{ flex: 1, minWidth: 120 }}
                disabled={loading}
              >
                {periodoOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
                {customRange ? (
                  <MenuItem value="custom">Personalizado</MenuItem>
                ) : null}
              </TextField>
              <BiDateRangeField
                value={customRange}
                onApply={(r) => setCustomRange(r)}
                onClear={() => setCustomRange(null)}
                disabled={loading}
                sx={{ flex: 1, minWidth: 160 }}
              />
              <CanalFilterField
                value={canalSelect}
                options={marketplaceOptions}
                onChange={setCanalFilter}
                disabled={loading}
                sx={{ flex: 1, minWidth: 100 }}
              />
            </>
          ) : (
            <>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                {periodoOptions.map((opt) => (
                  <Chip
                    key={opt.id}
                    size="medium"
                    label={opt.label}
                    color={!customRange && periodo === opt.id ? 'primary' : 'default'}
                    variant={!customRange && periodo === opt.id ? 'filled' : 'outlined'}
                    onClick={() => setPeriodo(opt.id as Periodo)}
                    disabled={loading && !customRange && periodo === opt.id}
                    sx={{
                      height: 36,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      px: 0.5,
                      '& .MuiChip-label': { px: 1.5 },
                    }}
                  />
                ))}
              </Stack>
              <BiDateRangeField
                value={customRange}
                onApply={(r) => setCustomRange(r)}
                onClear={() => setCustomRange(null)}
                disabled={loading}
              />
              <CanalFilterField
                value={canalSelect}
                options={marketplaceOptions}
                onChange={setCanalFilter}
                disabled={loading}
                sx={{ minWidth: 180, ml: { md: 'auto' } }}
              />
            </>
          )}
        </Stack>
      </Box>
      ) : null}

      <Box
        component="main"
        sx={{
          flex: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          pt: { xs: 1.5, sm: 2 },
          pb: { xs: 'calc(88px + env(safe-area-inset-bottom, 0px))', sm: 3 },
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {!isSystemRoute && emptyPeriod ? (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setPeriodo('7d')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Últimos 7 dias
              </Button>
            }
          >
            {weekToDateOnlyToday
              ? '«Esta semana» na segunda-feira cobre só hoje — ainda sem vendas. Clique em Últimos 7 dias para ver o histórico.'
              : 'Sem vendas neste intervalo. Clique em Últimos 7 dias ou escolha outro período.'}
          </Alert>
        ) : null}
        {!isSystemRoute && !isFiestaActive && activeSource && Number(activeSource.pedidos_count || 0) <= 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Fonte <strong>{activeSource.name}</strong> ({activeSource.db_name}) — ainda sem pedidos
            carregados no espelho. Os KPIs ficam vazios até a carga desta fonte concluir.
          </Alert>
        ) : null}
        {!isSystemRoute && !isFiestaActive && activeSource && Number(activeSource.pedidos_count || 0) > 0 ? (
          <Alert severity="success" sx={{ mb: 2 }} variant="outlined">
            Fonte <strong>{activeSource.name}</strong> — a ler{' '}
            <code>{activeSource.db_name}</code> (
            {Number(activeSource.pedidos_count).toLocaleString('pt-BR')} pedidos). Números isolados
            do Fiesta.
          </Alert>
        ) : null}
        <RequirePanel>
          <Outlet />
        </RequirePanel>
        {/* Alerta de SKU esgotado vendável: popup em qualquer página (services/vigia-esgotados). */}
        <VigiaEsgotadosPopup />
      </Box>
    </Box>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        pt: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {showBiSidebar ? (
        <>
          {/* Sidebar desktop — fixed (paridade Financial: não some ao rolar). */}
          <Drawer
            variant="permanent"
            open
            sx={{
              display: { xs: 'none', md: 'block' },
              width: BI_DRAWER_WIDTH,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                ...drawerPaperSx,
                position: 'fixed',
                top: 0,
                left: 0,
                height: '100%',
                // Acima do modal do chat (recolhido ou expandido) — menu sempre clicável
                zIndex: (t) =>
                  assistantOpen ? t.zIndex.modal + 2 : t.zIndex.drawer,
              },
            }}
          >
            <DomainNav onNavigate={closeAssistant} />
          </Drawer>

          {/* Sidebar mobile — temporary */}
          <Drawer
            variant="temporary"
            open={navOpen}
            onClose={() => setNavOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              [`& .MuiDrawer-paper`]: drawerPaperSx,
            }}
          >
            <DomainNav
              onNavigate={() => {
                setNavOpen(false)
                closeAssistant()
              }}
            />
          </Drawer>
        </>
      ) : null}

      {contentColumn}

      <UiVisibilityDialog />

      {isMobile ? (
        <Fab
          color="primary"
          variant="extended"
          onClick={openAssistantManually}
          aria-label="Abrir assistente Fiesta IA"
          sx={{
            position: 'fixed',
            right: 16,
            bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            zIndex: (t) => t.zIndex.speedDial,
            textTransform: 'none',
            fontWeight: 700,
            boxShadow: 4,
          }}
        >
          Fiesta IA
        </Fab>
      ) : null}

      {/* Paridade Financial Dashboard: botão «reduzir» fixed na junção sidebar|chat
          (fora do modal — z-index correcto). Em embed=1 o pai S2 desenha o botão. */}
      {assistantOpen && assistantExpanded && !isMobile && !embedded ? (
        <IconButton
          aria-label="reduzir chat"
          onClick={() => setAssistantExpanded(false)}
          sx={{
            position: 'fixed',
            left: showBiSidebar && !isNavMobile ? BI_DRAWER_WIDTH : 0,
            // Centro da área abaixo do header (não do ecrã inteiro)
            top: `calc((100vh + ${BI_APP_BAR_HEIGHT}) / 2)`,
            zIndex: (t) => t.zIndex.modal + 3,
            width: 40,
            height: 48,
            transform: 'translate(-50%, -50%)',
            borderRadius: '18px 8px 8px 18px',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: '0 2px 10px rgba(15, 23, 42, 0.22)',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          <Hicon name="chevron-right" sx={{ fontSize: 22 }} />
        </IconButton>
      ) : null}

      {/* Standalone: chat abaixo do header (paridade Financial). Embed: iframe já está sob o AppBar S2. */}
      <Drawer
        anchor="right"
        open={assistantOpen}
        onClose={closeAssistant}
        ModalProps={{
          keepMounted: true,
          // Sidebar desktop sempre fora do modal/backdrop (recolhido e expandido),
          // como no Financial onde o menu S2 fica fora do iframe.
          sx: rightDrawerModalSx,
        }}
        PaperProps={{
          sx: {
            width: {
              xs: '100%',
              sm: assistantExpanded ? '100%' : 452,
            },
            maxWidth: '100%',
            top: embedded ? 0 : BI_APP_BAR_HEIGHT,
            height: embedded ? '100%' : `calc(100% - ${BI_APP_BAR_HEIGHT})`,
            overflow: 'visible',
            transition: (t) =>
              t.transitions.create(['width', 'top', 'height'], { duration: 220 }),
            ...(showBiSidebar && !isNavMobile && assistantExpanded
              ? {
                  width: '100%',
                  maxWidth: embedded
                    ? '100%'
                    : `calc(100vw - ${BI_DRAWER_WIDTH}px)`,
                }
              : {}),
          },
        }}
      >
        <AssistantDrawer
          onClose={closeAssistant}
          initialPrompt={assistantPrompt}
          expanded={assistantExpanded}
          onToggleExpand={isMobile ? undefined : () => setAssistantExpanded((v) => !v)}
        />
      </Drawer>

      {/* Insights da IA do painel aberto — botão «Insights» do cabeçalho. Monta só
          quando abre: as leituras por domínio são pedidas na hora. */}
      <Drawer
        anchor="right"
        open={insightsAberto}
        onClose={fecharInsights}
        ModalProps={{ sx: rightDrawerModalSx }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 452 },
            maxWidth: '100%',
            top: embedded ? 0 : BI_APP_BAR_HEIGHT,
            height: embedded ? '100%' : `calc(100% - ${BI_APP_BAR_HEIGHT})`,
          },
        }}
      >
        <PanelInsightsDrawer onClose={fecharInsights} />
      </Drawer>
    </Box>
  )
}
